import * as React from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Container from '@mui/material/Container';
import Divider from '@mui/material/Divider';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import Alert from '@mui/material/Alert';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import FormControlLabel from '@mui/material/FormControlLabel';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import RefreshIcon from '@mui/icons-material/Refresh';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';
import CampaignOutlinedIcon from '@mui/icons-material/CampaignOutlined';
import DeleteForeverOutlinedIcon from '@mui/icons-material/DeleteForeverOutlined';
import { alpha } from '@mui/material/styles';
import AuthService from '../services/auth';
import apiClient, { getErrorMessage, shouldRedirectToLogin } from '../services/api-client';
import { isMultiTowerEnabled, getTowers } from '../services/unit-utils';
import CustomSnackbar from '../components/CustomSnackbar';
import PageHeader from '../components/PageHeader';

const ROLE_OPTIONS = [
  { value: 'admin', label: 'Administrador' },
  { value: 'habitante', label: 'Residente' },
  { value: 'receptor', label: 'Recepción' },
];

const ROLE_LABEL_MAP = ROLE_OPTIONS.reduce((acc, role) => ({ ...acc, [role.value]: role.label }), {});
const ALL_ROLE_VALUES = ROLE_OPTIONS.map((role) => role.value);

function getRoleLabels(values) {
  if (!Array.isArray(values) || values.length === 0) {
    return 'todos';
  }
  return values.map((value) => ROLE_LABEL_MAP[value] || value).join(', ');
}

const STATUS_LABELS = {
  active: 'Activo',
  scheduled: 'Programado',
  cancelled: 'Cancelado',
  draft: 'Borrador',
};

const STATUS_COLORS = {
  active: 'primary',
  scheduled: 'info',
  cancelled: 'default',
  draft: 'default',
};

const EMPTY_FORM = {
  title: '',
  main_message: '',
  sub_message: '',
  target_roles: ['admin', 'habitante', 'receptor'],
  target_unit_patterns: [],
  expires_at_input: '',
  publish_mode: 'now',
  publish_at_input: '',
  unit_mode: 'simple',
  simple_all: true,
  simple_towers: {},
  simple_extra: [],
  unit_patterns_raw: '',
};

// Remove spaces and leading dashes; '*' alone means "all units in the tower".
function sanitizeSimpleSuffix(value) {
  return String(value || '')
    .replace(/\s+/g, '')
    .replace(/^-+/, '');
}

// Compile simple-mode selection (all / per-tower suffix / leftover advanced
// patterns) into the target_unit_patterns array sent to the API.
function compileSimplePatterns(simpleAll, simpleTowers, simpleExtra, towers) {
  const extra = Array.isArray(simpleExtra) ? [...simpleExtra] : [];
  if (simpleAll) {
    return extra;
  }
  const compiled = [];
  (towers || []).forEach((tower) => {
    if (!Object.prototype.hasOwnProperty.call(simpleTowers || {}, tower)) {
      return;
    }
    let suffix = sanitizeSimpleSuffix(simpleTowers[tower]);
    if (!suffix || suffix === '*') {
      suffix = '*';
    }
    compiled.push(`${tower}-${suffix}`);
  });
  return [...extra, ...compiled];
}

// Canonical simple-mode shapes: "T-*" (whole tower), "T-x" (exact unit),
// "T-x*" (unit prefix) — with T being a currently configured tower.
function parsePatternsToSimple(patterns, towers) {
  const list = Array.isArray(patterns) ? patterns : [];
  if (list.length === 0) {
    return { ok: true, all: true, towers: {}, extra: [] };
  }
  const towerMap = {};
  const extra = [];
  const lowerTowers = (towers || []).map((tower) => String(tower).toLowerCase());
  list.forEach((pattern) => {
    const raw = String(pattern || '');
    const dashIndex = raw.indexOf('-');
    let matched = false;
    if (dashIndex > 0) {
      const towerPart = raw.slice(0, dashIndex);
      const suffix = raw.slice(dashIndex + 1);
      const towerIndex = lowerTowers.indexOf(towerPart.toLowerCase());
      // suffix has no dash and at most one trailing '*'
      const canonical = suffix === '*' || /^[^*-]*\*?$/.test(suffix);
      if (towerIndex >= 0 && canonical) {
        const tower = towers[towerIndex];
        const cleanSuffix = suffix === '*' ? '' : suffix;
        if (!Object.prototype.hasOwnProperty.call(towerMap, tower)) {
          towerMap[tower] = cleanSuffix;
          matched = true;
        } else if (towerMap[tower] === cleanSuffix) {
          matched = true; // exact duplicate
        }
      }
    }
    if (!matched) {
      extra.push(raw);
    }
  });
  return { ok: extra.length === 0, all: false, towers: towerMap, extra };
}

function formatCountdown(ts) {
  const diffMs = Number(ts) * 1000 - Date.now();
  if (!Number.isFinite(diffMs)) {
    return '';
  }
  if (diffMs <= 0) {
    return 'ahora';
  }
  const totalMinutes = Math.floor(diffMs / 60000);
  const days = Math.floor(totalMinutes / (60 * 24));
  const hours = Math.floor((totalMinutes % (60 * 24)) / 60);
  const minutes = totalMinutes % 60;
  if (days > 0) {
    return `en ${days} d ${hours} h`;
  }
  if (hours > 0) {
    return `en ${hours} h ${minutes} min`;
  }
  return `en ${Math.max(minutes, 1)} min`;
}

function formatDateTimeInput(ts) {
  if (!ts) {
    return '';
  }
  const date = new Date(Number(ts) * 1000);
  if (Number.isNaN(date.getTime())) {
    return '';
  }
  const offsetMs = date.getTimezoneOffset() * 60000;
  return new Date(date.getTime() - offsetMs).toISOString().slice(0, 16);
}

function toUnixTimestamp(value) {
  if (!value) {
    return null;
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return null;
  }
  return Math.round(date.getTime() / 1000);
}

function PatternCode({ children }) {
  return (
    <Box
      component="code"
      sx={{
        fontFamily: 'monospace',
        fontSize: '0.85em',
        px: 0.5,
        py: 0.125,
        borderRadius: 0.75,
        bgcolor: (theme) => alpha(theme.palette.text.primary, 0.08),
      }}
    >
      {children}
    </Box>
  );
}

function formatDisplayDate(ts) {
  if (!ts) {
    return '—';
  }
  try {
    return new Date(Number(ts) * 1000).toLocaleString('es-GT', {
      year: 'numeric',
      month: 'short',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch (error) {
    return '—';
  }
}

export default function AdminBannerNotifications({ setNavbarState, navbarState }) {
  const navigate = useNavigate();
  const [redirect, setRedirect] = React.useState({ trigger: false, route: '/' });
  const [loading, setLoading] = React.useState(true);
  const [saving, setSaving] = React.useState(false);
  const [notifications, setNotifications] = React.useState([]);
  const [form, setForm] = React.useState(EMPTY_FORM);
  const [editingId, setEditingId] = React.useState('');
  const [editingStatus, setEditingStatus] = React.useState('');
  const [helpOpen, setHelpOpen] = React.useState(false);
  const [error, setError] = React.useState('');
  const [totalUsers, setTotalUsers] = React.useState(null);
  const [snackbar, setSnackbar] = React.useState({ open: false, message: '', severity: 'success' });
  const [, setNowTick] = React.useState(Date.now());
  const multiTower = React.useMemo(() => isMultiTowerEnabled(), []);
  const towers = React.useMemo(() => getTowers(), []);

  const fetchNotifications = React.useCallback(async () => {
    try {
      setLoading(true);
      const response = await apiClient.get('admin/banner_notifications', {}, { action: 'fetch_admin_banner_notifications' });
      setNotifications(response?.data?.data?.notifications || []);
      setError('');
    } catch (fetchError) {
      if (shouldRedirectToLogin(fetchError)) {
        setRedirect({ trigger: true, route: '/login' });
        return;
      }
      setError(getErrorMessage(fetchError, 'No pude cargar las notificaciones.'));
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchTotalUsers = React.useCallback(async () => {
    try {
      const response = await apiClient.get('users/allusersadmin', {}, { action: 'fetch_all_users_admin_for_banner_notifications' });
      const users = response?.data?.data;
      if (Array.isArray(users)) {
        setTotalUsers(users.length);
      }
    } catch (fetchError) {
      if (shouldRedirectToLogin(fetchError)) {
        setRedirect({ trigger: true, route: '/login' });
        return;
      }
      setTotalUsers(null);
    }
  }, []);

  React.useEffect(() => {
    setNavbarState({ ...navbarState, showBack: true, showOptions: false, showSearch: false });
    fetchNotifications();
    fetchTotalUsers();
  }, []);

  // Refresh "Publica: ..." countdowns every 30s.
  React.useEffect(() => {
    const interval = setInterval(() => setNowTick(Date.now()), 30000);
    return () => clearInterval(interval);
  }, []);

  const resetForm = React.useCallback(() => {
    setForm(EMPTY_FORM);
    setEditingId('');
    setEditingStatus('');
  }, []);

  const handleChange = React.useCallback((key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  }, []);

  const handleRoleToggle = React.useCallback((roleValue) => {
    setForm((prev) => {
      const current = Array.isArray(prev.target_roles) ? prev.target_roles : [];
      const exists = current.includes(roleValue);
      return {
        ...prev,
        target_roles: exists ? current.filter((item) => item !== roleValue) : [...current, roleValue],
      };
    });
  }, []);

  // Advanced mode: raw text is the source of truth, split into patterns on change.
  const handleUnitPatternsChange = React.useCallback((value) => {
    const raw = String(value || '');
    setForm((prev) => ({
      ...prev,
      unit_patterns_raw: raw,
      target_unit_patterns: raw
        .split(',')
        .map((item) => item.trim())
        .filter(Boolean),
    }));
  }, []);

  // Simple mode: recompile patterns whenever the selection changes.
  const handleSimpleAllToggle = React.useCallback(() => {
    setForm((prev) => {
      const nextAll = !prev.simple_all;
      return {
        ...prev,
        simple_all: nextAll,
        target_unit_patterns: compileSimplePatterns(nextAll, prev.simple_towers, prev.simple_extra, towers),
        unit_patterns_raw: compileSimplePatterns(nextAll, prev.simple_towers, prev.simple_extra, towers).join(', '),
      };
    });
  }, [towers]);

  const handleTowerToggle = React.useCallback((tower) => {
    setForm((prev) => {
      const nextTowers = { ...(prev.simple_towers || {}) };
      if (Object.prototype.hasOwnProperty.call(nextTowers, tower)) {
        delete nextTowers[tower];
      } else {
        nextTowers[tower] = '';
      }
      const compiled = compileSimplePatterns(false, nextTowers, prev.simple_extra, towers);
      return {
        ...prev,
        simple_all: false,
        simple_towers: nextTowers,
        target_unit_patterns: compiled,
        unit_patterns_raw: compiled.join(', '),
      };
    });
  }, [towers]);

  const handleTowerSuffixChange = React.useCallback((tower, value) => {
    setForm((prev) => {
      const nextTowers = { ...(prev.simple_towers || {}), [tower]: value };
      const compiled = compileSimplePatterns(prev.simple_all, nextTowers, prev.simple_extra, towers);
      return {
        ...prev,
        simple_towers: nextTowers,
        target_unit_patterns: compiled,
        unit_patterns_raw: compiled.join(', '),
      };
    });
  }, [towers]);

  // Switching modes never loses patterns: advanced keeps the raw text,
  // simple keeps un-representable patterns in simple_extra.
  const handleUnitModeChange = React.useCallback((mode) => {
    if (!mode) {
      return;
    }
    setForm((prev) => {
      if (mode === prev.unit_mode) {
        return prev;
      }
      if (mode === 'advanced') {
        return {
          ...prev,
          unit_mode: 'advanced',
          unit_patterns_raw: (prev.target_unit_patterns || []).join(', '),
        };
      }
      const parsed = parsePatternsToSimple(prev.target_unit_patterns || [], towers);
      const compiled = compileSimplePatterns(parsed.all, parsed.towers, parsed.extra, towers);
      return {
        ...prev,
        unit_mode: 'simple',
        simple_all: parsed.all,
        simple_towers: parsed.towers,
        simple_extra: parsed.extra,
        target_unit_patterns: compiled,
        unit_patterns_raw: compiled.join(', '),
      };
    });
  }, [towers]);

  const isTargetingAllUsers = React.useCallback((notification) => {
    const targetRoles = Array.isArray(notification?.target_roles) ? notification.target_roles : [];
    const targetPatterns = Array.isArray(notification?.target_unit_patterns) ? notification.target_unit_patterns : [];
    const normalizedRoles = targetRoles.map((role) => String(role || '').toLowerCase()).filter(Boolean);
    const hasAllRoles = ALL_ROLE_VALUES.every((role) => normalizedRoles.includes(role)) || normalizedRoles.length === 0;
    return hasAllRoles && targetPatterns.length === 0;
  }, []);

  const getTargetedCount = React.useCallback((notification) => {
    if (isTargetingAllUsers(notification) && Number.isFinite(totalUsers)) {
      return totalUsers;
    }
    return notification?.stats?.targeted || 0;
  }, [isTargetingAllUsers, totalUsers]);

  const submitPayload = React.useMemo(() => {
    const expiresAt = toUnixTimestamp(form.expires_at_input);
    const payload = {
      title: String(form.title || '').trim(),
      main_message: String(form.main_message || '').trim(),
      sub_message: String(form.sub_message || '').trim(),
      target_roles: Array.isArray(form.target_roles) ? form.target_roles : [],
      target_unit_patterns: Array.isArray(form.target_unit_patterns) ? form.target_unit_patterns : [],
      ...(expiresAt ? { expires_at: expiresAt } : {}),
    };
    if (form.publish_mode === 'schedule') {
      const publishAt = toUnixTimestamp(form.publish_at_input);
      if (publishAt) {
        payload.publish_at = publishAt;
      }
    } else if (editingId && editingStatus === 'scheduled') {
      // Switching a scheduled announcement to "Publicar ahora" publishes it.
      payload.publish_at = Math.round(Date.now() / 1000);
    }
    return payload;
  }, [form, editingId, editingStatus]);

  const handleSubmit = React.useCallback(async () => {
    if (!submitPayload.title || !submitPayload.main_message) {
      setSnackbar({ open: true, message: 'Título y mensaje principal son obligatorios.', severity: 'warning' });
      return;
    }
    if (!submitPayload.target_roles.length) {
      setSnackbar({ open: true, message: 'Selecciona al menos un rol objetivo.', severity: 'warning' });
      return;
    }
    if (form.publish_mode === 'schedule') {
      if (!submitPayload.publish_at) {
        setSnackbar({ open: true, message: 'Define la fecha de publicación.', severity: 'warning' });
        return;
      }
      if (submitPayload.publish_at <= Math.round(Date.now() / 1000)) {
        setSnackbar({ open: true, message: 'La fecha de publicación debe ser futura.', severity: 'warning' });
        return;
      }
      if (submitPayload.expires_at && submitPayload.expires_at <= submitPayload.publish_at) {
        setSnackbar({ open: true, message: 'La expiración debe ser posterior a la publicación.', severity: 'warning' });
        return;
      }
    }
    try {
      setSaving(true);
      if (editingId) {
        await apiClient.put(`admin/banner_notifications/${editingId}`, submitPayload, {}, { action: 'update_admin_banner_notification' });
        setSnackbar({ open: true, message: 'Notificación actualizada.', severity: 'success' });
      } else {
        await apiClient.post('admin/banner_notifications', submitPayload, {}, { action: 'create_admin_banner_notification' });
        setSnackbar({ open: true, message: 'Notificación creada.', severity: 'success' });
      }
      resetForm();
      fetchNotifications();
    } catch (saveError) {
      if (shouldRedirectToLogin(saveError)) {
        setRedirect({ trigger: true, route: '/login' });
        return;
      }
      setSnackbar({ open: true, message: getErrorMessage(saveError, 'No pude guardar la notificación.'), severity: 'error' });
    } finally {
      setSaving(false);
    }
  }, [editingId, fetchNotifications, resetForm, submitPayload, form.publish_mode]);

  const handleEdit = React.useCallback((notification) => {
    setEditingId(notification?.id || '');
    setEditingStatus(notification?.status || '');
    const patterns = Array.isArray(notification?.target_unit_patterns) ? notification.target_unit_patterns : [];
    const isScheduled = notification?.status === 'scheduled';
    let unitFields = {
      unit_mode: 'advanced',
      unit_patterns_raw: patterns.join(', '),
      simple_all: patterns.length === 0,
      simple_towers: {},
      simple_extra: [],
    };
    if (multiTower) {
      const parsed = parsePatternsToSimple(patterns, towers);
      if (parsed.ok) {
        unitFields = {
          unit_mode: 'simple',
          unit_patterns_raw: patterns.join(', '),
          simple_all: parsed.all,
          simple_towers: parsed.towers,
          simple_extra: [],
        };
      }
    }
    setForm({
      title: notification?.title || '',
      main_message: notification?.main_message || '',
      sub_message: notification?.sub_message || '',
      target_roles: Array.isArray(notification?.target_roles) ? notification.target_roles : [],
      target_unit_patterns: patterns,
      expires_at_input: formatDateTimeInput(notification?.expires_at),
      publish_mode: isScheduled ? 'schedule' : 'now',
      publish_at_input: isScheduled ? formatDateTimeInput(notification?.publish_at) : '',
      ...unitFields,
    });
  }, [multiTower, towers]);

  const handleCancelNotification = React.useCallback(async (notificationId) => {
    try {
      await apiClient.post(`admin/banner_notifications/${notificationId}/cancel`, {}, {}, { action: 'cancel_admin_banner_notification' });
      setSnackbar({ open: true, message: 'Notificación cancelada.', severity: 'success' });
      fetchNotifications();
      if (editingId === notificationId) {
        resetForm();
      }
    } catch (cancelError) {
      if (shouldRedirectToLogin(cancelError)) {
        setRedirect({ trigger: true, route: '/login' });
        return;
      }
      setSnackbar({ open: true, message: getErrorMessage(cancelError, 'No pude cancelar la notificación.'), severity: 'error' });
    }
  }, [editingId, fetchNotifications, resetForm]);

  const handleDeleteNotification = React.useCallback(async (notificationId) => {
    if (!notificationId) {
      return;
    }
    const confirmed = window.confirm('Eliminar el anuncio de forma permanente? Esta acción no se puede deshacer.');
    if (!confirmed) {
      return;
    }
    try {
      await apiClient.delete(`admin/banner_notifications/${notificationId}`, {}, { action: 'delete_admin_banner_notification' });
      setSnackbar({ open: true, message: 'Notificación eliminada.', severity: 'success' });
      fetchNotifications();
      if (editingId === notificationId) {
        resetForm();
      }
    } catch (deleteError) {
      if (shouldRedirectToLogin(deleteError)) {
        setRedirect({ trigger: true, route: '/login' });
        return;
      }
      setSnackbar({ open: true, message: getErrorMessage(deleteError, 'No pude eliminar la notificación.'), severity: 'error' });
    }
  }, [editingId, fetchNotifications, resetForm]);

  const renderNotificationCard = (notification) => {
    const status = notification.status || 'active';
    const isScheduled = status === 'scheduled';
    const statCards = [
      { label: 'Objetivo', value: getTargetedCount(notification) },
      { label: 'Entregadas', value: notification.stats?.delivered || 0 },
      { label: 'Vistas', value: notification.stats?.viewed || 0 },
      { label: 'Descartadas', value: notification.stats?.dismissed || 0 },
      { label: 'Pendientes', value: notification.stats?.pending || 0 },
    ];

    return (
      <Card key={notification.id} sx={{ borderRadius: '10px', boxShadow: 0, border: '1px solid', borderColor: 'divider' }}>
        <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
          <Stack spacing={1.5}>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2 }}>
              <Box sx={{ minWidth: 0 }}>
                <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.75, flexWrap: 'wrap', useFlexGap: true }}>
                  <CampaignOutlinedIcon sx={{ color: 'primary.main' }} />
                  <Typography sx={{ fontSize: 17, fontWeight: 800, color: 'text.primary' }}>
                    {notification.title || 'Sin título'}
                  </Typography>
                  <Chip
                    label={STATUS_LABELS[status] || status}
                    size="small"
                    color={STATUS_COLORS[status] || 'default'}
                  />
                </Stack>
                <Typography sx={{ fontSize: 14, fontWeight: 600, color: 'text.secondary' }}>
                  {notification.main_message || notification.sub_message || 'Sin contenido'}
                </Typography>
              </Box>
              <Stack direction="row" spacing={1}>
                <IconButton onClick={() => handleEdit(notification)} aria-label="Editar">
                  <EditOutlinedIcon />
                </IconButton>
                {status !== 'cancelled' && (
                  <IconButton color="error" onClick={() => handleCancelNotification(notification.id)} aria-label="Cancelar">
                    <CancelOutlinedIcon />
                  </IconButton>
                )}
                {status === 'cancelled' && (
                  <IconButton color="error" onClick={() => handleDeleteNotification(notification.id)} aria-label="Eliminar">
                    <DeleteForeverOutlinedIcon />
                  </IconButton>
                )}
              </Stack>
            </Box>

            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              <Chip label={`Roles: ${getRoleLabels(notification.target_roles)}`} size="small" variant="outlined" />
              <Chip label={`Unidades: ${(notification.target_unit_patterns || []).join(', ') || 'todas'}`} size="small" variant="outlined" />
              {isScheduled && (
                <Chip
                  label={`Publica: ${formatDisplayDate(notification.publish_at)} (${formatCountdown(notification.publish_at)})`}
                  size="small"
                  color="info"
                  variant="outlined"
                />
              )}
              <Chip label={`Expira: ${formatDisplayDate(notification.expires_at)}`} size="small" variant="outlined" />
            </Stack>

            <Divider />

            {isScheduled ? (
              <Typography sx={{ fontSize: 13, fontWeight: 600, color: 'text.secondary' }}>
                Todavía no publicado
              </Typography>
            ) : (
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: { xs: 'repeat(2, minmax(0, 1fr))', md: 'repeat(5, minmax(0, 1fr))' },
                  gap: 1.5,
                }}
              >
                {statCards.map((stat) => (
                  <Box
                    key={stat.label}
                    sx={{
                      p: 1.5,
                      borderRadius: 2,
                      border: '1px solid',
                      borderColor: 'divider',
                      bgcolor: 'background.default',
                    }}
                  >
                    <Typography sx={{ fontSize: 12, fontWeight: 700, color: 'text.secondary' }}>{stat.label}</Typography>
                    <Typography sx={{ fontSize: 18, fontWeight: 900, color: 'text.primary' }}>{stat.value}</Typography>
                  </Box>
                ))}
              </Box>
            )}
          </Stack>
        </CardContent>
      </Card>
    );
  };

  if (!AuthService.userRoleIncludes(['admin'])) {
    return <Navigate to="/" replace={true} />;
  }

  if (redirect.trigger) {
    return <Navigate to={redirect.route} replace={true} />;
  }

  const scheduledNotifications = notifications.filter((item) => item?.status === 'scheduled');
  const otherNotifications = notifications.filter((item) => item?.status !== 'scheduled');
  const publishField = (
    <Box sx={{ gridColumn: '1 / -1' }}>
      <Typography sx={{ fontSize: 13, fontWeight: 700, color: 'text.secondary', mb: 0.5 }}>
        Publicación
      </Typography>
      <RadioGroup
        row
        value={form.publish_mode}
        onChange={(event) => handleChange('publish_mode', event.target.value)}
      >
        <FormControlLabel value="now" control={<Radio size="small" />} label="Publicar ahora" />
        <FormControlLabel
          value="schedule"
          control={<Radio size="small" />}
          label="Programar"
          disabled={Boolean(editingId) && editingStatus !== 'scheduled'}
        />
      </RadioGroup>
      {form.publish_mode === 'schedule' && (
        <TextField
          label="Publicar el"
          type="datetime-local"
          value={form.publish_at_input}
          onChange={(event) => handleChange('publish_at_input', event.target.value)}
          InputLabelProps={{ shrink: true }}
          helperText="Si no defines expiración, será 1 mes después de la publicación."
          fullWidth
        />
      )}
    </Box>
  );

  return (
    <Container maxWidth="sm" disableGutters>
      <Box sx={{ minHeight: '100vh', bgcolor: 'background.default', pb: 12 }}>
        <Box
          sx={{
            position: 'sticky',
            top: 0,
            zIndex: 10,
            px: { xs: 2, sm: 3 },
            pt: 2.5,
            pb: 2,
            backgroundColor: (theme) => alpha(theme.palette.background.paper, 0.95),
            backdropFilter: 'blur(10px)',
            borderBottom: '1px solid',
            borderColor: 'divider',
          }}
        >
          <Stack direction="row" spacing={1.5} alignItems="center" justifyContent="space-between">
            <PageHeader
              title="Anunciar"
              icon="bell-bing"
              onBack={() => navigate(-1)}
              subtitle="Crea, edita y cancela anuncios para administradores, residentes y recepción."
            />
            <Tooltip title="Refrescar">
              <span>
                <IconButton aria-label="Refrescar" onClick={fetchNotifications} disabled={loading} sx={{ color: 'text.secondary' }}>
                  <RefreshIcon
                    sx={loading ? {
                      animation: 'refreshSpin 1s linear infinite',
                      '@keyframes refreshSpin': {
                        '0%': { transform: 'rotate(0deg)' },
                        '100%': { transform: 'rotate(360deg)' },
                      },
                    } : undefined}
                  />
                </IconButton>
              </span>
            </Tooltip>
          </Stack>
        </Box>

        <Box sx={{ px: { xs: 2, sm: 3 }, pt: 3 }}>
          <Stack spacing={3}>
            {error && <Alert severity="error">{error}</Alert>}

            <Card sx={{ borderRadius: '10px', boxShadow: 0, border: '1px solid', borderColor: 'divider' }}>
              <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                <Stack spacing={2.5}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
                    <Box>
                      <Typography sx={{ fontSize: 18, fontWeight: 800, color: 'text.primary' }}>
                        {editingId ? 'Editar notificación' : 'Nueva notificación'}
                      </Typography>
                      <Typography sx={{ fontSize: 13, fontWeight: 600, color: 'text.secondary' }}>
                        Expiración por defecto: 1 mes si no defines una fecha.
                      </Typography>
                    </Box>
                    {editingId && (
                      <Button variant="outlined" onClick={resetForm}>
                        Cancelar edición
                      </Button>
                    )}
                  </Box>

                  <Box
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))' },
                      gap: 2,
                    }}
                  >
                    <Box>
                      <TextField label="Título" value={form.title} onChange={(event) => handleChange('title', event.target.value)} fullWidth />
                    </Box>
                    <Box>
                      <TextField
                        label="Expira en"
                        type="datetime-local"
                        value={form.expires_at_input}
                        onChange={(event) => handleChange('expires_at_input', event.target.value)}
                        InputLabelProps={{ shrink: true }}
                        fullWidth
                      />
                    </Box>
                    <Box sx={{ gridColumn: '1 / -1' }}>
                      <TextField
                        label="Mensaje principal"
                        value={form.main_message}
                        onChange={(event) => handleChange('main_message', event.target.value)}
                        fullWidth
                        multiline
                        minRows={2}
                      />
                    </Box>
                    <Box sx={{ gridColumn: '1 / -1' }}>
                      <TextField
                        label="Mensaje secundario"
                        value={form.sub_message}
                        onChange={(event) => handleChange('sub_message', event.target.value)}
                        fullWidth
                        multiline
                        minRows={2}
                      />
                    </Box>
                    <Box sx={{ gridColumn: '1 / -1' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1, flexWrap: 'wrap' }}>
                        <Typography sx={{ fontSize: 13, fontWeight: 700, color: 'text.secondary' }}>
                          Unidades
                        </Typography>
                        <Tooltip title="Cómo se filtran las unidades">
                          <IconButton
                            size="small"
                            aria-label="Ayuda sobre filtros de unidad"
                            onClick={() => setHelpOpen(true)}
                            sx={{ color: 'text.secondary', p: 0.25 }}
                          >
                            <HelpOutlineIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        {multiTower && (
                          <ToggleButtonGroup
                            value={form.unit_mode}
                            exclusive
                            size="small"
                            onChange={(event, value) => handleUnitModeChange(value)}
                            sx={{
                              ml: 'auto',
                              '& .MuiToggleButton-root': {
                                px: 1.5,
                                py: 0.25,
                                fontSize: '0.75rem',
                                fontWeight: 700,
                                whiteSpace: 'nowrap',
                              },
                            }}
                          >
                            <ToggleButton value="simple">Simple</ToggleButton>
                            <ToggleButton value="advanced">Avanzado</ToggleButton>
                          </ToggleButtonGroup>
                        )}
                      </Box>

                      {multiTower && form.unit_mode === 'simple' ? (
                        <Stack spacing={1.5}>
                          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                            <Chip
                              label="Todas las torres y unidades"
                              color={form.simple_all ? 'primary' : 'default'}
                              variant={form.simple_all ? 'filled' : 'outlined'}
                              onClick={handleSimpleAllToggle}
                            />
                          </Stack>
                          {!form.simple_all && (
                            <Stack spacing={1}>
                              {towers.map((tower) => {
                                const selected = Object.prototype.hasOwnProperty.call(form.simple_towers || {}, tower);
                                return (
                                  <Box key={tower} sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                                    <Chip
                                      label={tower}
                                      color={selected ? 'primary' : 'default'}
                                      variant={selected ? 'filled' : 'outlined'}
                                      onClick={() => handleTowerToggle(tower)}
                                    />
                                    {selected && (
                                      <TextField
                                        size="small"
                                        label={`Unidades de ${tower}`}
                                        placeholder="* = todas, 704, 7*"
                                        value={form.simple_towers[tower] || ''}
                                        onChange={(event) => handleTowerSuffixChange(tower, event.target.value)}
                                        sx={{ flex: 1, minWidth: 160 }}
                                      />
                                    )}
                                  </Box>
                                );
                              })}
                            </Stack>
                          )}
                          <Box>
                            <Typography sx={{ fontSize: 12, fontWeight: 700, color: 'text.secondary', mb: 0.5 }}>
                              Patrones resultantes
                            </Typography>
                            {(form.target_unit_patterns || []).length === 0 ? (
                              <Typography sx={{ fontSize: 13, color: 'text.secondary' }}>
                                Todas las unidades.
                              </Typography>
                            ) : (
                              <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
                                {(form.target_unit_patterns || []).map((pattern, index) => (
                                  <Chip
                                    key={`${pattern}-${index}`}
                                    label={String(pattern).toUpperCase()}
                                    size="small"
                                    variant="outlined"
                                    sx={{ fontFamily: 'monospace' }}
                                  />
                                ))}
                              </Stack>
                            )}
                          </Box>
                        </Stack>
                      ) : (
                        <TextField
                          label={multiTower ? 'Patrones de unidad' : 'Unidades (filtro avanzado)'}
                          value={form.unit_patterns_raw}
                          onChange={(event) => handleUnitPatternsChange(event.target.value)}
                          helperText={
                            multiTower
                              ? 'Opcional. Usa comas, por ejemplo: T1-*, T1-7*, *-704'
                              : 'Opcional. Usa comas, por ejemplo: 7*, 704'
                          }
                          fullWidth
                        />
                      )}
                    </Box>
                    {publishField}
                  </Box>

                  <Box>
                    <Typography sx={{ fontSize: 13, fontWeight: 700, color: 'text.secondary', mb: 1 }}>
                      Roles objetivo
                    </Typography>
                    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                      {ROLE_OPTIONS.map((role) => {
                        const selected = (form.target_roles || []).includes(role.value);
                        return (
                          <Chip
                            key={role.value}
                            label={role.label}
                            color={selected ? 'primary' : 'default'}
                            variant={selected ? 'filled' : 'outlined'}
                            onClick={() => handleRoleToggle(role.value)}
                          />
                        );
                      })}
                    </Stack>
                  </Box>

                  <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1.5 }}>
                    <Button variant="outlined" onClick={resetForm} disabled={saving}>
                      Limpiar
                    </Button>
                    <Button variant="contained" onClick={handleSubmit} disabled={saving}>
                      {saving ? 'Guardando...' : editingId ? 'Guardar cambios' : 'Crear notificación'}
                    </Button>
                  </Box>
                </Stack>
              </CardContent>
            </Card>

            <Card sx={{ borderRadius: '10px', boxShadow: 0, border: '1px solid', borderColor: 'divider' }}>
              <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                <Stack spacing={2}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
                    <Typography sx={{ fontSize: 18, fontWeight: 800, color: 'text.primary' }}>
                      Anuncios activos e históricos
                    </Typography>
                  </Box>

                  {!loading && scheduledNotifications.length > 0 && (
                    <Box>
                      <Typography sx={{ fontSize: 13, fontWeight: 700, color: 'text.secondary', mb: 1 }}>
                        Programados
                      </Typography>
                      <Stack spacing={2}>
                        {scheduledNotifications.map((notification) => renderNotificationCard(notification))}
                      </Stack>
                    </Box>
                  )}

                  {loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                      <CircularProgress />
                    </Box>
                  ) : otherNotifications.length === 0 ? (
                    scheduledNotifications.length === 0 ? (
                      <Alert severity="info">Aún no hay anuncios.</Alert>
                    ) : null
                  ) : (
                    <Stack spacing={2}>
                      {otherNotifications.map((notification) => renderNotificationCard(notification))}
                    </Stack>
                  )}
                </Stack>
              </CardContent>
            </Card>
          </Stack>
        </Box>

        <Dialog open={helpOpen} onClose={() => setHelpOpen(false)} maxWidth="sm" fullWidth>
          <DialogTitle sx={{ fontWeight: 800 }}>Cómo se filtran las unidades</DialogTitle>
          <DialogContent>
            <Stack spacing={1.5} sx={{ pt: 0.5 }}>
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                Los filtros de rol y unidad se combinan — la persona debe cumplir ambos.
              </Typography>
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                Sin patrones, el anuncio incluye todas las unidades.
              </Typography>
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                <PatternCode>*</PatternCode> reemplaza cualquier cantidad de caracteres.
              </Typography>
              {multiTower ? (
                <Box component="ul" sx={{ m: 0, pl: 2.5, color: 'text.secondary' }}>
                  <Typography component="li" variant="body2">
                    <PatternCode>T1-*</PatternCode> incluye toda la torre T1
                  </Typography>
                  <Typography component="li" variant="body2">
                    <PatternCode>T1-7*</PatternCode> incluye las unidades de T1 que empiezan con 7
                  </Typography>
                  <Typography component="li" variant="body2">
                    <PatternCode>*-704</PatternCode> incluye la unidad 704 en cualquier torre
                  </Typography>
                </Box>
              ) : (
                <Box component="ul" sx={{ m: 0, pl: 2.5, color: 'text.secondary' }}>
                  <Typography component="li" variant="body2">
                    <PatternCode>7*</PatternCode> incluye las unidades que empiezan con 7
                  </Typography>
                  <Typography component="li" variant="body2">
                    <PatternCode>704</PatternCode> incluye solo la unidad 704
                  </Typography>
                </Box>
              )}
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                Con varios patrones basta que la unidad coincida con uno.
              </Typography>
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                Los usuarios sin unidad no reciben anuncios con filtro de unidad.
              </Typography>
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setHelpOpen(false)}>Cerrar</Button>
          </DialogActions>
        </Dialog>

        <CustomSnackbar
          open={snackbar.open}
          message={snackbar.message}
          severity={snackbar.severity}
          onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        />
      </Box>
    </Container>
  );
}
